import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'da' | 'en' | 'sv';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  da: {
    // Nav & General
    'nav.dashboard': 'Dashboard',
    'nav.documents': 'Dokumenter',
    'nav.templates': 'Skabeloner',
    'nav.forms': 'KvikForm',
    'nav.kviksend': 'KvikSend',
    'nav.kvikid': 'KvikID',
    'nav.profile': 'Min profil',
    'nav.settings': 'Indstillinger',
    'nav.superadmin': 'Superadmin panel',
    'nav.logout': 'Log ud',
    'nav.language': 'Sprog',
    'nav.account': 'Konto',
    'nav.accounts': 'Konto',
    'nav.back_to_dashboard': 'Tilbage til dashboard',
    
    // Actions
    'action.send_document': 'Send dokument',
    'action.create_form': 'Opret formular',
    'action.save': 'Gem',
    'action.cancel': 'Annuller',
    'action.search': 'Søg...',
    'action.filter': 'Filtrér',
    'action.delete': 'Slet',
    'action.edit': 'Rediger',
    
    // Status
    'status.draft': 'Udkast',
    'status.sent': 'Sendt',
    'status.completed': 'Gennemført',
    'status.pending': 'Afventer',
    'status.rejected': 'Afvist',

    // Language names
    'lang.da': 'Dansk 🇩🇰',
    'lang.en': 'English 🇬🇧',
    'lang.sv': 'Svenska 🇸🇪',
    'Masseforsendelse': 'Masseforsendelse',
  },
  en: {
    // Nav & General
    'nav.dashboard': 'Dashboard',
    'nav.documents': 'Documents',
    'nav.templates': 'Templates',
    'nav.forms': 'KvikForm',
    'nav.kviksend': 'KvikSend',
    'nav.kvikid': 'KvikID',
    'nav.profile': 'My Profile',
    'nav.settings': 'Settings',
    'nav.superadmin': 'Superadmin Panel',
    'nav.logout': 'Log out',
    'nav.language': 'Language',
    'nav.account': 'Account',
    'nav.accounts': 'Account',
    'nav.back_to_dashboard': 'Back to Dashboard',
    
    // Actions
    'action.send_document': 'Send Document',
    'action.create_form': 'Create Form',
    'action.save': 'Save',
    'action.cancel': 'Cancel',
    'action.search': 'Search...',
    'action.filter': 'Filter',
    'action.delete': 'Delete',
    'action.edit': 'Edit',
    
    // Status
    'status.draft': 'Draft',
    'status.sent': 'Sent',
    'status.completed': 'Completed',
    'status.pending': 'Pending',
    'status.rejected': 'Rejected',

    // Language names
    'lang.da': 'Danish 🇩🇰',
    'lang.en': 'English 🇬🇧',
    'lang.sv': 'Swedish 🇸🇪',
    'Masseforsendelse': 'Bulk send',
  },
  sv: {
    // Nav & General
    'nav.dashboard': 'Översikt',
    'nav.documents': 'Dokument',
    'nav.templates': 'Mallar',
    'nav.forms': 'KvikForm',
    'nav.kviksend': 'KvikSend',
    'nav.kvikid': 'KvikID',
    'nav.profile': 'Min profil',
    'nav.settings': 'Inställningar',
    'nav.superadmin': 'Superadmin-panel',
    'nav.logout': 'Logga ut',
    'nav.language': 'Språk',
    'nav.account': 'Konto',
    'nav.accounts': 'Konto',
    'nav.back_to_dashboard': 'Tillbaka till översikten',
    
    // Actions
    'action.send_document': 'Skicka dokument',
    'action.create_form': 'Skapa formulär',
    'action.save': 'Spara',
    'action.cancel': 'Avbryt',
    'action.search': 'Sök...',
    'action.filter': 'Filtrera',
    'action.delete': 'Ta bort',
    'action.edit': 'Redigera',
    
    // Status
    'status.draft': 'Utkast',
    'status.sent': 'Skickat',
    'status.completed': 'Slutförd',
    'status.pending': 'Väntar',
    'status.rejected': 'Avvisad',

    // Language names
    'lang.da': 'Danska 🇩🇰',
    'lang.en': 'Engelska 🇬🇧',
    'lang.sv': 'Svenska 🇸🇪',
    'Masseforsendelse': 'Massutskick',
  }
};

const LanguageContext = createContext<LanguageContextType>({
  language: 'da',
  setLanguage: () => {},
  t: (key: string, fallback?: string) => fallback || key,
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const p = window.location.pathname.toLowerCase();
      if (p.startsWith('/eng') || p.startsWith('/en')) return 'en';
      if (p.startsWith('/sv') || p.startsWith('/se')) return 'sv';
      if (p.startsWith('/dk') || p.startsWith('/da')) return 'da';
    }
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('kviksign_language') : null;
    return (saved === 'en' || saved === 'da' || saved === 'sv') ? (saved as Language) : 'da';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('kviksign_language', lang);
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  };

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  // Sync language with URL if path changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const checkPathLang = () => {
      const p = window.location.pathname.toLowerCase();
      if (p.startsWith('/eng') || p.startsWith('/en')) {
        setLanguageState('en');
      } else if (p.startsWith('/sv') || p.startsWith('/se')) {
        setLanguageState('sv');
      } else if (p.startsWith('/dk') || p.startsWith('/da')) {
        setLanguageState('da');
      }
    };
    window.addEventListener('popstate', checkPathLang);
    return () => window.removeEventListener('popstate', checkPathLang);
  }, []);

const autoTranslateMap: Record<string, string> = {
  // Navigation & General
  'Dashboard': 'Dashboard',
  'Transaktioner': 'Transactions',
  'Kladder': 'Drafts',
  'Konto': 'Account',
  'Design': 'Design',
  'Brugere': 'Users',
  'Grupper': 'Groups',
  'Formularer': 'Forms',
  'Abonnement': 'Subscription',
  'Faktura': 'Invoices',
  'Beskeder': 'Messages',
  'Min profil': 'My Profile',
  'Indstillinger': 'Settings',
  'Superadmin panel': 'Superadmin Panel',
  'Log ud': 'Log Out',
  'Sprog': 'Language',
  'Dokumenter': 'Documents',
  'Skabeloner': 'Templates',
  'Send dokument': 'Send document',
  'Opret formular': 'Create form',
  'Gem': 'Save',
  'Annuller': 'Cancel',
  'Gennemført': 'Completed',
  'Afventer': 'Pending',
  'Afvist': 'Declined',
  'Annulleret': 'Cancelled',
  'Udløbet': 'Expired',
  'Udkast': 'Draft',
  'Leveret': 'Delivered',
  'Fejlet': 'Failed',
  'Rediger': 'Edit',
  'Slet': 'Delete',
  'Søg...': 'Search...',
  'Tilbage til dashboard': 'Back to dashboard',
  'Ny transaktion': 'New transaction',
  'Opret ny transaktion': 'Create new transaction',
  'Send til underskrift': 'Send for signature',
  'Transaktionsoversigt': 'Transaction Overview',
  'Alle transaktioner': 'All transactions',
  'Seneste aktivitet': 'Recent activity',
  'Se prisplaner': 'View pricing plans',
  'Priser': 'Pricing',
  'Søg efter dokumenter...': 'Search documents...',
  'Afventer underskrift': 'Awaiting signature',
  'Alle har underskrevet': 'All signed',
  'Har underskrevet': 'Signed',
  'MitID verifikation': 'MitID verification',
  'Kontoinformation': 'Account Information',
  'E-mail skabeloner': 'Email Templates',
  'API Nøgler': 'API Keys',
  'Sikkerhed': 'Security',
  'Filtre': 'Filters',
  'Kolonner': 'Columns',
  'Vælg kolonner': 'Select columns',
  'Nulstil': 'Reset',
  'Vis alle': 'Show all',
  'Kompakt': 'Compact',
  'Titel': 'Title',
  'Type': 'Type',
  'Oprettelsesdato': 'Creation Date',
  'Svarfrist / Udløb': 'Deadline / Expiry',
  'Nuværende Status': 'Current Status',
  'Modtagerliste': 'Recipient List',
  'Signeringsmetode': 'Signing Method',
  'Identifikationsmetode': 'Identification Method',
  'Distribution': 'Distribution',
  'Credits (Forbrug)': 'Credits (Usage)',
  'Arbejdsgang (Sekventiel)': 'Workflow (Sequential)',
  'Filstørrelse': 'File Size',
  'Påmindelsesfrekvens': 'Reminder Frequency',
  'Advisering': 'Notification',
  'Handlinger': 'Actions',
  'Modtagere': 'Recipients',
  'Afsender': 'Sender',
  'Dato': 'Date',
  'Ingen transaktioner fundet': 'No transactions found',
  'Ingen kladder fundet': 'No drafts found',
  'Filtrer efter status': 'Filter by status',
  'Filtrer efter type': 'Filter by type',
  'Nulstil filtre': 'Reset filters',
  'Gem som kladde': 'Save as draft',
  'Send': 'Send',
  'Luk': 'Close',
  'Tilbage': 'Back',
  'Næste': 'Next',
  'Generelt': 'General',
  'Revisionslog': 'Audit log',
  'Filer': 'Files',
  'Tilføj modtager': 'Add recipient',
  'Navn': 'Name',
  'E-mail': 'Email',
  'Telefon': 'Phone',
  'Mobilnummer': 'Mobile number',
  'CVR-nummer': 'CVR number',
  'Virksomhedsnavn': 'Company name',
  'Adresse': 'Address',
  'Postnummer': 'Zip code',
  'By': 'City',
  'Rolle': 'Role',
  'Aktiv': 'Active',
  'Inaktiv': 'Inactive',
  'Vis detaljer': 'View details',
  'Download PDF': 'Download PDF',
  'Download': 'Download',
  'Kopiér link': 'Copy link',
  'Send på ny': 'Resend',
  'Genaktiver': 'Reactivate',
  'Slet forsendelse': 'Delete shipment',
  'Annuller forsendelse': 'Cancel shipment',
  'Tilføj bruger': 'Add user',
  'Tilføj gruppe': 'Add group',
  'Upload logo': 'Upload logo',
  'Primær farve': 'Primary color',
  'Sekundær farve': 'Secondary color',
  'Gem ændringer': 'Save changes',
  'Personverifikation': 'Identity Verification',
  'Filoverførsel': 'File Transfer',
  'Sikker filoverførsel': 'Secure File Transfer',
  'Signering': 'Signing',
  'Formular': 'Form',
  'Velkommen tilbage': 'Welcome back',
  'Hurtig oversigt over dine seneste transaktioner og aktiviteter': 'Quick overview of your recent transactions and activities',
  'Sekventiel': 'Sequential',
  'Parallel': 'Parallel',
  'Identifikation': 'Identification',
  'Arbejdsgang': 'Workflow',
  'Ingen': 'None',
  'Ok-knap': 'OK Button',
  'ok knap': 'OK button',
  'SMS engangskode': 'SMS passcode',
  'Dato fra': 'Date from',
  'Dato til': 'Date to',
  'Visning': 'View',
  'Søg efter navne, e-mails eller titler...': 'Search by names, emails or titles...',
  'Opret ny forsendelse': 'Create new shipment',
  'Opret din første transaktion': 'Create your first transaction',
  'Du har ingen kladder gemt.': 'You have no saved drafts.',
  'Du har ingen aktive transaktioner endnu.': 'You have no active transactions yet.',
  'Opret ny kladde': 'Create new draft',
  'Rediger kladde': 'Edit draft',
  'Gennemførte transaktioner': 'Completed transactions',
  'Afventende transaktioner': 'Pending transactions',
  'Afviste / Annullerede': 'Declined / Cancelled',
  'Samlet forbrug': 'Total usage',
  'Aktive skabeloner': 'Active templates',
  'Brugere i organisationen': 'Users in organization',
  'Seneste 30 dage': 'Last 30 days',
  'Credits tilbage': 'Credits remaining',
  'Administrer': 'Manage',
  'Opgrader abonnement': 'Upgrade subscription',

  // Dashboard & Charts
  'Dokumentpakker i alt': 'Total document packages',
  'Færdigbehandlet': 'Completed',
  'Afventer handling': 'Awaiting action',
  'Forsendelsestendenser': 'Shipment trends',
  'Sidste 6 mdr.': 'Last 6 months',
  'Filoverførsler': 'File transfers',
  'Personverifikationer': 'Identity verifications',
  'Signaturer': 'Signatures',
  'Identitetsverificering (eID / CIBA)': 'Identity Verification (eID / CIBA)',
  'Anmodninger': 'Requests',
  'Godkendte': 'Approved',

  // DocumentsList / Search / Filters / Columns
  'Skabelon': 'Template',
  'Returdestination': 'Return destination',
  'Uden skabelon': 'No template',
  'Ingen skabelon': 'No template',
  'Dokumenter returneres til': 'Documents returned to',
  'Ingen transaktioner': 'No transactions',
  'Der blev ikke fundet nogle transaktioner, som matcher dine søgekriterier og aktive filtreringer.': 'No transactions were found matching your search criteria and active filters.',
  'Søg i titel, modtager, reference...': 'Search in title, recipient, reference...',
  'Søg i titel, modtager, reference.': 'Search in title, recipient, reference.',
  'Titel (Vigtig)': 'Title',
  'Titel / Dokument': 'Title / Document',
  'Type (Signering/Filoverførsel)': 'Type',
  'Reference / Mappe-ID': 'Reference',
  'Reference (ID)': 'Reference',
  'Svarfrist': 'Deadline',
  'Modtager(e)': 'Recipient(s)',
  'Oprettet': 'Created',
  'Rækkefølge': 'Order',
  'Fra dato': 'From date',
  'Til dato': 'To date',
  'Aktive filtre:': 'Active filters:',
  'Søg:': 'Search:',
  'Fra:': 'From:',
  'Til:': 'To:',
  'Status:': 'Status:',
  'Type:': 'Type:',
  'Metode:': 'Method:',
  'Identifikation:': 'Identification:',
  'Rækkefølge:': 'Order:',
  'Ryd datoer': 'Clear dates',
  'Klik': 'Click',
  'Ref:': 'Ref:',
  'andre': 'others',
  'Samlet forbrug:': 'Total usage:',
  'credits': 'credits',
  'FIL SLETTET (ANNULLERET)': 'FILE DELETED (CANCELLED)',
  'Gem oplysninger': 'Save info',
  'Gemmer...': 'Saving...',
  'Sender...': 'Sending...',
  'I kø': 'Queued',
  'Planlagt': 'Scheduled',
  'Set': 'Viewed',
  'Udfyldt': 'Filled',
  'Underskrevet': 'Signed',
  'Notifikation': 'Notification',
  'Ingen registrerede notifikationer på denne transaktion.': 'No registered notifications for this transaction.',
  'Ingen registrerede identifikationer på denne transaktion.': 'No registered identifications for this transaction.',
  'Ingen registrerede signeringer på denne transaktion.': 'No registered signatures for this transaction.',
  'Ingen registrerede distributionshandlinger på denne transaktion.': 'No registered distribution actions for this transaction.',
  'Prøver igen...': 'Retrying...',
  'Prøv at generere GlobalSign certifikat igen': 'Try generating GlobalSign certificate again',
  'GlobalSign Certifikatfejl – Underskrifter og ID\'er er gemt': 'GlobalSign Certificate Error – Signatures and IDs saved',
  'Leveringsfejl (fx soft-bounce / modtager fejl)': 'Delivery error (e.g. soft-bounce / recipient error)',
  'Ja, slet kladde': 'Yes, delete draft',
  'Ja, annuller forsendelse': 'Yes, cancel shipment',
  'Genaktiver forsendelse': 'Reactivate shipment',
  'Ja, genaktiver & send': 'Yes, reactivate & send',
  'Ryd alle': 'Clear all',
  'Nulstil alle filtre': 'Reset all filters',
  'Henter transaktionsdata...': 'Loading transaction data...',
  'Vent venligst mens alle detaljer for transaktionen indlæses fra databasen': 'Please wait while all details for the transaction are loaded from the database',
  'Underskrivere': 'Signers',
  'Forbrugt på denne dokumentpakke': 'Consumed on this document package',
  'Rediger indstillinger': 'Edit settings',
  'Rediger generelle indstillinger': 'Edit general settings',
  'Udløbsdato': 'Expiration date',
  'Rykkerfrekvens': 'Reminder frequency',
  'Notifikationsfrekvens': 'Notification frequency',
  'Planlagt forsendelse': 'Scheduled shipment',
  'Planlagt forsendelse (Dato & Tid)': 'Scheduled shipment (Date & Time)',
  'Signeringsrækkefølge': 'Signing order',
  'Forsendelsestype': 'Shipment type',
  'Sekventiel signering': 'Sequential signing',
  'Parallel signering': 'Parallel signing',
  'Med det samme': 'Immediately',
  'Ukendt Afsender': 'Unknown Sender',
  'Hver dag': 'Every day',
  'Hver 2. dag': 'Every 2 days',
  'Hver 3. dag': 'Every 3 days',
  'Hver uge': 'Every week',
  'Hver 2. uge': 'Every 2 weeks',
  'Send aldrig påmindelser': 'Never send reminders',
  'Rediger underskriver': 'Edit signer',
  'Simpel underskrift': 'Simple signature',
  'Simpel signatur': 'Simple signature',
  'Signeringsmetode:': 'Signing method:',
  'Identifikationsmetode:': 'Identification method:',
  'Rediger e-mail & send igen': 'Edit email & resend',
  'Gensend til': 'Resend to',
  'Gensend': 'Resend',
  'Kopier link': 'Copy link',
  'Kopieret!': 'Copied!',
  'Modtagers Afvisningsårsag': 'Recipient Rejection Reason',
  'Ingen yderligere kommentarer angivet.': 'No additional comments provided.',
  'Registreret:': 'Registered:',
  'Formularen er endnu ikke udfyldt af modtageren.': 'The form has not yet been filled out by the recipient.',
  'Ingen filer tilgængelige.': 'No files available.',
  'Vis dokument': 'Show document',
  'Skjul': 'Hide',
  'Download fil': 'Download file',
  'Hent fil': 'Download file',
  'Forhåndsvisning:': 'Preview:',
  'SIGNATUR SIKKERHEDSSTEMPEL': 'SIGNATURE SECURITY STAMP',
  'Følgende parter har underskrevet dette dokument digitalt via KvikSign Nordisk Digital Signeringstjeneste.': 'The following parties have digitally signed this document via KvikSign Nordic Digital Signing Service.',
  'Dato:': 'Date:',
  'Hash:': 'Hash:',
  'Henter revisionslog...': 'Fetching audit log...',
  'Filtrer efter part:': 'Filter by party:',
  'Vis alle hændelser': 'Show all events',
  'Relevante for:': 'Relevant for:',
  'Udskriv / Eksporter log': 'Print / Export log',
  'Ingen hændelser fundet for denne filtrering.': 'No events found for this filter.',
  'Viser': 'Showing',
  'til': 'to',
  'af': 'of',
  'hændelser': 'events',
  'Forrige': 'Previous',
  'Genaktiver & Forlæng frist': 'Reactivate & Extend deadline',
  'Genaktiverer...': 'Reactivating...',
  'Luk overblik': 'Close overview',
  'Er du sikker på, at du vil annullere forsendelsen': 'Are you sure you want to cancel the shipment',
  'Dette kan ikke fortrydes. Parter, der efterfølgende klikker på deres link, vil få besked om, at forsendelsen er annulleret, og ingen vil kunne underskrive.': 'This action cannot be undone. Parties who subsequently click their link will be notified that the shipment is cancelled, and no one will be able to sign.',

  // Send Wizard (/send)
  'Nyt dokument': 'New document',
  'CPR-nummer': 'CPR number',
  'Modtagers navn / reference (valgfri)': 'Recipient name / reference (optional)',
  'Sender til MitID...': 'Sending to MitID...',
  'Vælg Formular': 'Select Form',
  'Ingen aktive formularer fundet': 'No active forms found',
  'Gå til Formularbygger': 'Go to Form Builder',
  'Formular skabelon': 'Form template',
  'Træk og slip filer her': 'Drag and drop files here',
  'eller klik for at vælge fra din enhed': 'or click to choose from your device',
  'Understøtter PDF, DOCX (Maks 20MB)': 'Supports PDF, DOCX (Max 20MB)',
  'Understøtter alle filtyper (Maks 1000MB)': 'Supports all file types (Max 1000MB)',
  'Valgte filer': 'Selected files',
  'Underskrives': 'Requires signature',
  'Låst': 'Locked',
  'Flere udfyldere': 'Multiple fillers',
  'Tilføj': 'Add',
  'Tilføj ny modtager': 'Add',
  'none': 'none',
  'Ingen modtagere tilføjet endnu.': 'No recipients added yet.',
  'Kanal:': 'Channel:',
  'Udfylder formularen': 'Fills out the form',
  'Modtager efter udfyldelse': 'Recipient after completion',
  'Rediger modtager': 'Edit recipient',
  'Slet modtager': 'Delete recipient',
  'Formularfelter': 'Form fields',
  'Denne formular indeholder låste felter, som skal udfyldes af dig før afsendelse. Modtageren vil modtage formularen med disse felter udfyldt.': 'This form contains locked fields that must be filled out by you before sending. The recipient will receive the form with these fields filled out.',
  'Vis kun låste felter': 'Show only locked fields',
  'Vis alle felter': 'Show all fields',
  'Forsendelsesindstillinger': 'Shipment settings',
  'Frist og påmindelser': 'Deadline and reminders',
  'Indstil udløbsdato, planlagt afsendelse og automatiske rykkere': 'Set expiration date, scheduled shipment, and automatic reminders',
  'Vis indstillinger': 'Show settings',
  'Skjul indstillinger': 'Hide settings',
  'Referencenummer': 'Reference number',
  'Indtast referencenummer (f.eks. sagsnr)': 'Enter reference number (e.g. case no)',
  'Planlagt afsendelse': 'Scheduled shipment',
  'Straks': 'Immediately',
  'Straks (i dag)': 'Immediately (today)',
  'Rykkere (hver x. dag)': 'Reminders (every x days)',
  'Afbryd': 'Cancel',
  'Send via': 'Send via',
  'E-mail og SMS': 'Email and SMS',
  'Ingen (Direkte link / Manuel distribution)': 'None (Direct link / Manual distribution)',
  'Modtag kopi efter signering': 'Receive copy after signing',
  'Navn er et påkrævet felt.': 'Name is a required field.',
  'E-mailadresse er påkrævet.': 'Email address is required.',
  'Telefonnummer er påkrævet ved SMS engangskode / SMS-distribution.': 'Phone number is required for SMS passcode / SMS distribution.',
  'CPR-nummer er påkrævet til Personverifikation (CIBA).': 'CPR number is required for Identity Verification.',
  'Yderligere indstillinger': 'Additional settings',
  'Touchskærm': 'Touchscreen',
  'Skjult godkender': 'Hidden approver',

  // Settings & Subpages
  'Virksomhed': 'Company',
  'Virksomhedsoplysninger': 'Company Information',
  'Organisation & Brugere': 'Organization & Users',
  'Abonnement & Forbrug': 'Subscription & Usage',
  'API & Webhooks': 'API & Webhooks',
  'Branding & Design': 'Branding & Design',
  'Signeringsflow & Standarder': 'Signing Flow & Defaults',
  'KvikForm Konfiguration': 'KvikForm Configuration',
  'Fakturaer & Betaling': 'Invoices & Payment',
  'Visuelt Design': 'Visual Design',
  'Bruger administration': 'User Management',
  'Roller & Rettigheder': 'Roles & Permissions',
  'Rediger bruger': 'Edit User',
  'Opret ny bruger': 'Create New User',
  'Ny gruppe': 'New Group',
  'Gruppenavn': 'Group Name',
  'Beskrivelse': 'Description',
  'Medlemmer': 'Members',
  'Administrer brugere': 'Manage Users',
  'Administrer grupper': 'Manage Groups',
  'Skabelonnavn': 'Template Name',
  'Emne': 'Subject',
  'Indhold': 'Content',
  'Nulstil til standard': 'Reset to default',
  'Vælg skabelon': 'Select Template',
  'Formularnavn': 'Form Name',
  'Rediger profil': 'Edit Profile',
  'Adgangskode': 'Password',
  'To-faktor godkendelse (2FA)': 'Two-Factor Authentication (2FA)',
  'Sikkerhedslog': 'Security Log',
  'Gammel adgangskode': 'Current Password',
  'Ny adgangskode': 'New Password',
  'Bekræft ny adgangskode': 'Confirm New Password',
  'Opdater adgangskode': 'Update Password',
  'Abonnementstype': 'Subscription Plan',
  'Status på abonnement': 'Subscription Status',
  'Aktiv konto': 'Active Account',
  'Fornyelsesdato': 'Renewal Date',
  'Betalingsmetode': 'Payment Method',
  'Tilføj betalingskort': 'Add Payment Card',
  'Opdater kort': 'Update Card',
  'Fakturahistorik': 'Invoice History',
  'Download faktura': 'Download Invoice',
  'Betal nu': 'Pay Now',
  'Kreditkort': 'Credit Card',
  'Ingen fakturaer fundet': 'No invoices found',
  'Brugere & Rettigheder': 'Users & Permissions',
  'Opret Bruger': 'Create User',
  'Opret Gruppe': 'Create Group',
  'API Nøgle': 'API Key',
  'Generer ny nøgle': 'Generate new key',
  'Kopiér': 'Copy',
};

const autoTranslateMapSv: Record<string, string> = {
  // Navigation & General
  'Dashboard': 'Översikt',
  'Transaktioner': 'Transaktioner',
  'Kladder': 'Utkast',
  'Konto': 'Konto',
  'Design': 'Design',
  'Brugere': 'Användare',
  'Grupper': 'Grupper',
  'Formularer': 'Formulär',
  'Abonnement': 'Abonnemang',
  'Faktura': 'Fakturor',
  'Beskeder': 'Meddelanden',
  'Min profil': 'Min profil',
  'Indstillinger': 'Inställningar',
  'Superadmin panel': 'Superadmin-panel',
  'Log ud': 'Logga ut',
  'Sprog': 'Språk',
  'Dokumenter': 'Dokument',
  'Skabeloner': 'Mallar',
  'Send dokument': 'Skicka dokument',
  'Opret formular': 'Skapa formulär',
  'Gem': 'Spara',
  'Annuller': 'Avbryt',
  'Gennemført': 'Slutförd',
  'Afventer': 'Väntar',
  'Afvist': 'Avvisad',
  'Annulleret': 'Avbruten',
  'Udløbet': 'Utgången',
  'Udkast': 'Utkast',
  'Leveret': 'Levererad',
  'Fejlet': 'Misslyckades',
  'Rediger': 'Redigera',
  'Slet': 'Ta bort',
  'Søg...': 'Sök...',
  'Tilbage til dashboard': 'Tillbaka till översikten',
  'Ny transaktion': 'Ny transaktion',
  'Opret ny transaktion': 'Skapa ny transaktion',
  'Send til underskrift': 'Skicka för signering',
  'Transaktionsoversigt': 'Transaktionsöversikt',
  'Alle transaktioner': 'Alla transaktioner',
  'Seneste aktivitet': 'Senaste aktivitet',
  'Se prisplaner': 'Se prisplaner',
  'Priser': 'Priser',
  'Søg efter dokumenter...': 'Sök efter dokument...',
  'Afventer underskrift': 'Väntar på signering',
  'Alle har underskrevet': 'Alla har signerat',
  'Har underskrevet': 'Har signerat',
  'MitID verifikation': 'Identitetsverifiering',
  'Kontoinformation': 'Kontoinformation',
  'E-mail skabeloner': 'E-postmallar',
  'API Nøgler': 'API-nycklar',
  'Sikkerhed': 'Säkerhet',
  'Filtre': 'Filter',
  'Kolonner': 'Kolumner',
  'Vælg kolonner': 'Välj kolumner',
  'Nulstil': 'Återställ',
  'Vis alle': 'Visa alla',
  'Kompakt': 'Kompakt',
  'Titel': 'Titel',
  'Type': 'Typ',
  'Oprettelsesdato': 'Skapad datum',
  'Svarfrist / Udløb': 'Svarsfrist / Utgångsdatum',
  'Nuværende Status': 'Aktuell status',
  'Modtagerliste': 'Mottagarlista',
  'Signeringsmetode': 'Signeringsmetod',
  'Identifikationsmetode': 'Identifieringsmetod',
  'Distribution': 'Distribution',
  'Credits (Forbrug)': 'Credits (Förbrukning)',
  'Arbejdsgang (Sekventiel)': 'Arbetsflöde (Sekventiell)',
  'Filstørrelse': 'Filstorlek',
  'Påmindelsesfrekvens': 'Påminnelsefrekvens',
  'Advisering': 'Avisering',
  'Handlinger': 'Åtgärder',
  'Modtagere': 'Mottagare',
  'Afsender': 'Avsändare',
  'Dato': 'Datum',
  'Ingen transaktioner fundet': 'Inga transaktioner hittades',
  'Ingen kladder fundet': 'Inga utkast hittades',
  'Filtrer efter status': 'Filtrera efter status',
  'Filtrer efter type': 'Filtrera efter typ',
  'Nulstil filtre': 'Återställ filter',
  'Gem som kladde': 'Spara som utkast',
  'Send': 'Skicka',
  'Luk': 'Stäng',
  'Tilbage': 'Tillbaka',
  'Næste': 'Nästa',
  'Generelt': 'Allmänt',
  'Revisionslog': 'Revisionslogg',
  'Filer': 'Filer',
  'Tilføj modtager': 'Lägg till mottagare',
  'Navn': 'Namn',
  'E-mail': 'E-post',
  'Telefon': 'Telefon',
  'Mobilnummer': 'Mobilnummer',
  'CVR-nummer': 'Organisationsnummer',
  'Virksomhedsnavn': 'Företagsnamn',
  'Adresse': 'Adress',
  'Postnummer': 'Postnummer',
  'By': 'Ort',
  'Rolle': 'Roll',
  'Aktiv': 'Aktiv',
  'Inaktiv': 'Inaktiv',
  'Vis detaljer': 'Visa detaljer',
  'Download PDF': 'Ladda ner PDF',
  'Download': 'Ladda ner',
  'Kopiér link': 'Kopiera länk',
  'Send på ny': 'Skicka igen',
  'Genaktiver': 'Återaktivera',
  'Slet forsendelse': 'Ta bort försändelse',
  'Annuller forsendelse': 'Avbryt försändelse',
  'Tilføj bruger': 'Lägg till användare',
  'Tilføj gruppe': 'Lägg till grupp',
  'Upload logo': 'Ladda upp logotyp',
  'Primær farve': 'Primärfärg',
  'Sekundær farve': 'Sekundärfärg',
  'Gem ændringer': 'Spara ändringar',
  'Personverifikation': 'Identitetsverifiering',
  'Filoverførsel': 'Filöverföring',
  'Sikker filoverførsel': 'Säker filöverföring',
  'Signering': 'Signering',
  'Formular': 'Formulär',
  'Velkommen tilbage': 'Välkommen tillbaka',
  'Hurtig oversigt over dine seneste transaktioner og aktiviteter': 'Snabb översikt över dina senaste transaktioner och aktiviteter',
  'Sekventiel': 'Sekventiell',
  'Parallel': 'Parallell',
  'Identifikation': 'Identifiering',
  'Arbejdsgang': 'Arbetsflöde',
  'Ingen': 'Ingen',
  'Ok-knap': 'OK-knapp',
  'ok knap': 'OK-knapp',
  'SMS engangskode': 'SMS-engångskod',
  'Dato fra': 'Datum från',
  'Dato til': 'Datum till',
  'Visning': 'Vy',
  'Søg efter navne, e-mails eller titler...': 'Sök efter namn, e-post eller titlar...',
  'Opret ny forsendelse': 'Skapa ny försändelse',
  'Opret din første transaktion': 'Skapa din första transaktion',
  'Du har ingen kladder gemt.': 'Du har inga sparade utkast.',
  'Du har ingen aktive transaktioner endnu.': 'Du har inga aktiva transaktioner än.',
  'Opret ny kladde': 'Skapa nytt utkast',
  'Rediger kladde': 'Redigera utkast',
  'Gennemførte transaktioner': 'Slutförda transaktioner',
  'Afventende transaktioner': 'Väntande transaktioner',
  'Afviste / Annullerede': 'Avvisade / Avbrutna',
  'Samlet forbrug': 'Total förbrukning',
  'Aktive skabeloner': 'Aktiva mallar',
  'Brugere i organisationen': 'Användare i organisationen',
  'Seneste 30 dage': 'Senaste 30 dagarna',
  'Credits tilbage': 'Credits kvar',
  'Administrer': 'Hantera',
  'Opgrader abonnement': 'Uppgradera abonnemang',

  // Dashboard & Charts
  'Dokumentpakker i alt': 'Dokumentpaket totalt',
  'Færdigbehandlet': 'Slutförd',
  'Afventer handling': 'Väntar på åtgärd',
  'Forsendelsestendenser': 'Försändelsetrender',
  'Sidste 6 mdr.': 'Senaste 6 månaderna',
  'Filoverførsler': 'Filöverföringar',
  'Personverifikationer': 'Identitetsverifieringar',
  'Signaturer': 'Signaturer',
  'Identitetsverificering (eID / CIBA)': 'Identitetsverifiering (BankID / eID)',
  'Anmodninger': 'Förfrågningar',
  'Godkendte': 'Godkända',

  // DocumentsList / Search / Filters / Columns
  'Skabelon': 'Mall',
  'Returdestination': 'Returdestination',
  'Uden skabelon': 'Utan mall',
  'Ingen skabelon': 'Ingen mall',
  'Dokumenter returneres til': 'Dokument returneras till',
  'Ingen transaktioner': 'Inga transaktioner',
  'Der blev ikke fundet nogle transaktioner, som matcher dine søgekriterier og aktive filtreringer.': 'Inga transaktioner matchade dina sökkriterier och aktiva filter.',
  'Søg i titel, modtager, reference...': 'Sök i titel, mottagare, referens...',
  'Søg i titel, modtager, reference.': 'Sök i titel, mottagare, referens.',
  'Titel (Vigtig)': 'Titel',
  'Titel / Dokument': 'Titel / Dokument',
  'Type (Signering/Filoverførsel)': 'Typ',
  'Reference / Mappe-ID': 'Referens / Mapp-ID',
  'Reference (ID)': 'Referens (ID)',
  'Svarfrist': 'Svarsfrist',
  'Modtager(e)': 'Mottagare',
  'Oprettet': 'Skapad',
  'Rækkefølge': 'Ordning',
  'Fra dato': 'Från datum',
  'Til dato': 'Till datum',
  'Aktive filtre:': 'Aktiva filter:',
  'Søg:': 'Sök:',
  'Fra:': 'Från:',
  'Til:': 'Till:',
  'Status:': 'Status:',
  'Type:': 'Typ:',
  'Metode:': 'Metod:',
  'Identifikation:': 'Identifiering:',
  'Rækkefølge:': 'Ordning:',
  'Ryd datoer': 'Rensa datum',
  'Klik': 'Klicka',
  'Ref:': 'Ref:',
  'andre': 'andra',
  'Samlet forbrug:': 'Total förbrukning:',
  'credits': 'credits',
  'FIL SLETTET (ANNULLERET)': 'FIL RADERAD (AVBRUTEN)',
  'Gem oplysninger': 'Spara uppgifter',
  'Gemmer...': 'Sparar...',
  'Sender...': 'Skickar...',
  'I kø': 'I kö',
  'Planlagt': 'Schemalagd',
  'Set': 'Visad',
  'Udfyldt': 'Ifylld',
  'Underskrevet': 'Signerat',
  'Notifikation': 'Avisering',
  'Ingen registrerede notifikationer på denne transaktion.': 'Inga registrerade aviseringar för denna transaktion.',
  'Ingen registrerede identifikationer på denne transaktion.': 'Inga registrerade identifieringar för denna transaktion.',
  'Ingen registrerede signeringer på denne transaktion.': 'Inga registrerade signeringar för denna transaktion.',
  'Ingen registrerede distributionshandlinger på denne transaktion.': 'Inga registrerade distributionsåtgärder för denna transaktion.',
  'Prøver igen...': 'Försöker igen...',
  'Prøv at generere GlobalSign certifikat igen': 'Försök skapa GlobalSign-certifikat igen',
  'GlobalSign Certifikatfejl – Underskrifter og ID\'er er gemt': 'GlobalSign Certifikatfel – Signaturer och ID:n har sparats',
  'Leveringsfejl (fx soft-bounce / modtager fejl)': 'Leveransfel (t.ex. soft-bounce / mottagarfel)',
  'Ja, slet kladde': 'Ja, ta bort utkast',
  'Ja, annuller forsendelse': 'Ja, avbryt försändelse',
  'Genaktiver forsendelse': 'Återaktivera försändelse',
  'Ja, genaktiver & send': 'Ja, återaktivera och skicka',
  'Ryd alle': 'Rensa alla',
  'Nulstil alle filtre': 'Återställ alla filter',
  'Henter transaktionsdata...': 'Hämtar transaktionsdata...',
  'Vent venligst mens alle detaljer for transaktionen indlæses fra databasen': 'Vänta medan alla detaljer för transaktionen läses in från databasen',
  'Underskrivere': 'Undertecknare',
  'Forbrugt på denne dokumentpakke': 'Förbrukat på detta dokumentpaket',
  'Rediger indstillinger': 'Redigera inställningar',
  'Rediger generelle indstillinger': 'Redigera allmänna inställningar',
  'Udløbsdato': 'Utgångsdatum',
  'Rykkerfrekvens': 'Påminnelsefrekvens',
  'Notifikationsfrekvens': 'Aviseringsfrekvens',
  'Planlagt forsendelse': 'Schemalagd försändelse',
  'Planlagt forsendelse (Dato & Tid)': 'Schemalagd försändelse (Datum & Tid)',
  'Signeringsrækkefølge': 'Signeringsordning',
  'Forsendelsestype': 'Försändelsetyp',
  'Sekventiel signering': 'Sekventiell signering',
  'Parallel signering': 'Parallell signering',
  'Med det samme': 'Omedelbart',
  'Ukendt Afsender': 'Okänd avsändare',
  'Hver dag': 'Varje dag',
  'Hver 2. dag': 'Varannan dag',
  'Hver 3. dag': 'Var 3:e dag',
  'Hver uge': 'Varje vecka',
  'Hver 2. uge': 'Varannan vecka',
  'Send aldrig påmindelser': 'Skicka aldrig påminnelser',
  'Rediger underskriver': 'Redigera undertecknare',
  'Simpel underskrift': 'Enkel signatur',
  'Simpel signatur': 'Enkel signatur',
  'Signeringsmetode:': 'Signeringsmetod:',
  'Identifikationsmetode:': 'Identifieringsmetod:',
  'Rediger e-mail & send igen': 'Redigera e-post och skicka igen',
  'Gensend til': 'Skicka igen till',
  'Gensend': 'Skicka igen',
  'Kopier link': 'Kopiera länk',
  'Kopieret!': 'Kopierat!',
  'Modtagers Afvisningsårsag': 'Mottagarens orsak till avvisning',
  'Ingen yderligere kommentarer angivet.': 'Inga ytterligare kommentarer angavs.',
  'Registreret:': 'Registrerad:',
  'Formularen er endnu ikke udfyldt af modtageren.': 'Formuläret har ännu inte fyllts i av mottagaren.',
  'Ingen filer tilgængelige.': 'Inga filer tillgängliga.',
  'Vis dokument': 'Visa dokument',
  'Skjul': 'Dölj',
  'Download fil': 'Ladda ner fil',
  'Hent fil': 'Ladda ner fil',
  'Forhåndsvisning:': 'Förhandsvisning:',
  'SIGNATUR SIKKERHEDSSTEMPEL': 'SIGNATUR SÄKERHETSSTÄMPEL',
  'Følgende parter har underskrevet dette dokument digitalt via KvikSign Nordisk Digital Signeringstjeneste.': 'Följande parter har signerat detta dokument digitalt via KvikSign Nordisk Digital Signeringstjänst.',
  'Dato:': 'Datum:',
  'Hash:': 'Hash:',
  'Henter revisionslog...': 'Hämtar revisionslogg...',
  'Filtrer efter part:': 'Filtrera efter part:',
  'Vis alle hændelser': 'Visa alla händelser',
  'Relevante for:': 'Relevanta för:',
  'Udskriv / Eksporter log': 'Skriv ut / Exportera logg',
  'Ingen hændelser fundet for denne filtrering.': 'Inga händelser hittades för denna filtrering.',
  'Viser': 'Visar',
  'til': 'till',
  'af': 'av',
  'hændelser': 'händelser',
  'Forrige': 'Föregående',
  'Genaktiver & Forlæng frist': 'Återaktivera & Förläng frist',
  'Genaktiverer...': 'Återaktiverar...',
  'Luk overblik': 'Stäng översikt',
  'Er du sikker på, at du vil annullere forsendelsen': 'Är du säker på att du vill avbryta försändelsen',
  'Dette kan ikke fortrydes. Parter, der efterfølgende klikker på deres link, vil få besked om, at forsendelsen er annulleret, og ingen vil kunne underskrive.': 'Detta kan inte ångras. Parter som klickar på sin länk informeras om att försändelsen har avbrutits och ingen kommer att kunna signera.',

  // Send Wizard (/send)
  'Nyt dokument': 'Nytt dokument',
  'CPR-nummer': 'Personnummer',
  'Modtagers navn / reference (valgfri)': 'Mottagarens namn / referens (valfritt)',
  'Sender til MitID...': 'Skickar till BankID...',
  'Vælg Formular': 'Välj Formulär',
  'Ingen aktive formularer fundet': 'Inga aktiva formulär hittades',
  'Gå til Formularbygger': 'Gå till Formulärbyggaren',
  'Formular skabelon': 'Formulärmall',
  'Træk og slip filer her': 'Dra och släpp filer här',
  'eller klik for at vælge fra din enhed': 'eller klicka för att välja från din enhet',
  'Understøtter PDF, DOCX (Maks 20MB)': 'Stöder PDF, DOCX (Max 20MB)',
  'Understøtter alle filtyper (Maks 1000MB)': 'Stöder alla filtyper (Max 1000MB)',
  'Valgte filer': 'Valda filer',
  'Underskrives': 'Ska signeras',
  'Låst': 'Låst',
  'Flere udfyldere': 'Flera som fyller i',
  'Tilføj': 'Lägg till',
  'Tilføj ny modtager': 'Lägg till',
  'none': 'ingen',
  'Ingen modtagere tilføjet endnu.': 'Inga mottagare har lagts till än.',
  'Kanal:': 'Kanal:',
  'Udfylder formularen': 'Fyller i formuläret',
  'Modtager efter udfyldelse': 'Mottagare efter ifyllning',
  'Rediger modtager': 'Redigera mottagare',
  'Slet modtager': 'Ta bort mottagare',
  'Formularfelter': 'Formulärfält',
  'Denne formular indeholder låste felter, som skal udfyldes af dig før afsendelse. Modtageren vil modtage formularen med disse felter udfyldt.': 'Detta formulär innehåller låsta fält som måste fyllas i av dig innan det skickas. Mottagaren får formuläret med dessa fält ifyllda.',
  'Vis kun låste felter': 'Visa endast låsta fält',
  'Vis alle felter': 'Visa alla fält',
  'Forsendelsesindstillinger': 'Försändelseinställningar',
  'Frist og påmindelser': 'Tidsfrist och påminnelser',
  'Indstil udløbsdato, planlagt afsendelse og automatiske rykkere': 'Ställ in utgångsdatum, schemalagd avsändning och automatiska påminnelser',
  'Vis indstillinger': 'Visa inställningar',
  'Skjul indstillinger': 'Dölj inställningar',
  'Referencenummer': 'Referensnummer',
  'Indtast referencenummer (f.eks. sagsnr)': 'Ange referensnummer (t.ex. ärendenummer)',
  'Planlagt afsendelse': 'Schemalagd avsändning',
  'Straks': 'Omedelbart',
  'Straks (i dag)': 'Omedelbart (idag)',
  'Rykkere (hver x. dag)': 'Påminnelser (var x:e dag)',
  'Afbryd': 'Avbryt',
  'Send via': 'Skicka via',
  'E-mail og SMS': 'E-post och SMS',
  'Ingen (Direkte link / Manuel distribution)': 'Ingen (Direktlänk / Manuell distribution)',
  'Modtag kopi efter signering': 'Få kopia efter signering',
  'Navn er et påkrævet felt.': 'Namn är ett obligatoriskt fält.',
  'E-mailadresse er påkrævet.': 'E-postadress är obligatorisk.',
  'Telefonnummer er påkrævet ved SMS engangskode / SMS-distribution.': 'Telefonnummer krävs för SMS-engångskod / SMS-distribution.',
  'CPR-nummer er påkrævet til Personverifikation (CIBA).': 'Personnummer krävs för identitetsverifiering.',
  'Yderligere indstillinger': 'Ytterligare inställningar',
  'Touchskærm': 'Pekskärm',
  'Skjult godkender': 'Dold godkännare',

  // Settings & Subpages
  'Virksomhed': 'Företag',
  'Virksomhedsoplysninger': 'Företagsuppgifter',
  'Organisation & Brugere': 'Organisation & Användare',
  'Abonnement & Forbrug': 'Abonnemang & Förbrukning',
  'API & Webhooks': 'API & Webhooks',
  'Branding & Design': 'Branding & Design',
  'Signeringsflow & Standarder': 'Signeringsflöde & Standarder',
  'KvikForm Konfiguration': 'KvikForm Konfiguration',
  'Fakturaer & Betaling': 'Fakturor & Betalning',
  'Visuelt Design': 'Visuell Design',
  'Bruger administration': 'Användarhantering',
  'Roller & Rettigheder': 'Roller & Behörigheter',
  'Rediger bruger': 'Redigera användare',
  'Opret ny bruger': 'Skapa ny användare',
  'Ny gruppe': 'Ny grupp',
  'Gruppenavn': 'Gruppnamn',
  'Beskrivelse': 'Beskrivning',
  'Medlemmer': 'Medlemmar',
  'Administrer brugere': 'Hantera användare',
  'Administrer grupper': 'Hantera grupper',
  'Skabelonnavn': 'Mallnamn',
  'Emne': 'Ämne',
  'Indhold': 'Innehåll',
  'Nulstil til standard': 'Återställ till standard',
  'Vælg skabelon': 'Välj mall',
  'Formularnavn': 'Formulärnamn',
  'Rediger profil': 'Redigera profil',
  'Adgangskode': 'Lösenord',
  'To-faktor godkendelse (2FA)': 'Tvåfaktorsautentisering (2FA)',
  'Sikkerhedslog': 'Säkerhetslogg',
  'Gammel adgangskode': 'Nuvarande lösenord',
  'Ny adgangskode': 'Nytt lösenord',
  'Bekræft ny adgangskode': 'Bekräfta nytt lösenord',
  'Opdater adgangskode': 'Uppdatera lösenord',
  'Abonnementstype': 'Abonnemangstyp',
  'Status på abonnement': 'Abonnemangsstatus',
  'Aktiv konto': 'Aktivt konto',
  'Fornyelsesdato': 'Förnyelsedatum',
  'Betalingsmetode': 'Betalningsmetod',
  'Tilføj betalingskort': 'Lägg till betalkort',
  'Opdater kort': 'Uppdatera kort',
  'Fakturahistorik': 'Fakturahistorik',
  'Download faktura': 'Ladda ner faktura',
  'Betal nu': 'Betala nu',
  'Kreditkort': 'Kreditkort',
  'Ingen fakturaer fundet': 'Inga fakturor hittades',
  'Brugere & Rettigheder': 'Användare & Behörigheter',
  'Opret Bruger': 'Skapa användare',
  'Opret Gruppe': 'Skapa grupp',
  'API Nøgle': 'API-nyckel',
  'Generer ny nøgle': 'Skapa ny nyckel',
  'Kopiér': 'Kopiera',
  'Sprog for modtager': 'Språk för mottagare',
  'Dansk': 'Danska',
  'Engelsk': 'Engelska',
  'Svensk': 'Svenska',
  'Svenska': 'Svenska',
  'Rækkefølge for underskrift': 'Signeringsordning'
};

  const t = (key: string, fallback?: string): string => {
    if (translations[language] && translations[language][key]) {
      return translations[language][key];
    }
    if (language === 'da') {
      if (translations.da[key]) return translations.da[key];
      return key;
    }
    if (language === 'sv') {
      if (translations.sv && translations.sv[key]) {
        return translations.sv[key];
      }
      const textToTranslate = fallback || key;
      if (autoTranslateMapSv[textToTranslate]) {
        return autoTranslateMapSv[textToTranslate];
      }
      if (autoTranslateMapSv[key]) {
        return autoTranslateMapSv[key];
      }
      return fallback || key;
    }
    // Language is 'en'
    if (translations.en[key]) {
      return translations.en[key];
    }
    const textToTranslate = fallback || key;
    if (autoTranslateMap[textToTranslate]) {
      return autoTranslateMap[textToTranslate];
    }
    if (autoTranslateMap[key]) {
      return autoTranslateMap[key];
    }
    return fallback || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
